import {
  ChangeDetectionStrategy,
  Component,
  input,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  Check,
  X,
  AlertTriangle,
  FileText,
  Users,
  CircleDot,
} from 'lucide-angular';
import { MaterialsAnalysisPreview, ResourcePreview } from '../../grading.types';
import { ImagePreviewModalComponent, PreviewItem } from '../image-preview-modal/image-preview-modal.component';

@Component({
  selector: 'app-materials-analysis-preview',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, ImagePreviewModalComponent],
  templateUrl: './materials-analysis-preview.component.html',
  styleUrl: './materials-analysis-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialsAnalysisPreviewComponent {
  readonly preview = input.required<MaterialsAnalysisPreview>();

  protected previewItem: PreviewItem | null = null;

  protected readonly icons = {
    Check,
    X,
    AlertTriangle,
    FileText,
    Users,
    CircleDot,
  };

  /**
   * Check if resource is an image
   */
  protected isImage(resource: ResourcePreview): boolean {
    return resource.mimeType?.startsWith('image/') ?? false;
  }

  /**
   * Open image preview modal
   */
  protected openPreview(resource: ResourcePreview, event: Event): void {
    if (this.isImage(resource)) {
      event.preventDefault();
      event.stopPropagation();
      this.previewItem = {
        url: resource.url,
        fileName: resource.name,
        mimeType: resource.mimeType,
      };
    }
  }

  /**
   * Close image preview modal
   */
  protected closePreview(): void {
    this.previewItem = null;
  }

  /**
   * Get remaining count if more than 5 files
   */
  protected getRemainingCount(totalCount: number, previewCount: number): number {
    return Math.max(0, totalCount - previewCount);
  }

  /**
   * Materials as a list for iteration
   */
  protected readonly materialsList = computed(() => {
    const p = this.preview();
    return [
      {
        label: 'Questions',
        present: p.materials.hasQuestions,
        required: true,
      },
      {
        label: 'Student Work',
        present: p.materials.hasStudentWork,
        required: true,
        count: p.materials.studentWorkCount,
      },
      {
        label: 'Answer Key',
        present: p.materials.hasAnswerKey,
        required: false,
      },
      {
        label: 'Rubric',
        present: p.materials.hasRubric,
        required: false,
      },
    ];
  });

  /**
   * Get the title based on status
   */
  protected getTitle(): string {
    const p = this.preview();
    if (p.isReadyForGrading) {
      return 'Materials Ready';
    }
    return 'Materials Analysis';
  }

  /**
   * Get the subtitle based on status
   */
  protected getSubtitle(): string {
    const p = this.preview();
    if (p.isReadyForGrading) {
      const studentCount = p.studentMappings.length;
      const fileCount = p.materials.studentWorkCount;
      return `${studentCount} student${studentCount !== 1 ? 's' : ''}, ${fileCount} file${fileCount !== 1 ? 's' : ''} ready`;
    }
    if (p.missingRequired.length > 0) {
      return `Missing: ${p.missingRequired.join(', ')}`;
    }
    return 'Analyzing files...';
  }

  /**
   * Format student name for display
   */
  protected formatStudentName(student: MaterialsAnalysisPreview['studentMappings'][0]): string {
    if (student.lastName) {
      return `${student.firstName} ${student.lastName}`;
    }
    return student.firstName;
  }

  /**
   * Get file count text
   */
  protected getFileCountText(count: number): string {
    return count === 1 ? '1 file' : `${count} files`;
  }
}
